import React, { useState, useCallback, memo } from 'react';
import { Dropdown } from '../../../../components/Dropdown';
import { APIKeyService } from '../../../../services/APIKeyService';
import { APIKeyStatus, APIKey } from '../../../../entities/APIKey';
import { SpanTooltip } from '../../../../components/SpanTooltip';
import { IconButton } from '../../../../components/IconButton';
import { EditAPIKeyNameModal } from '../APIKeyModal/EditAPIKeyNameModal';
import { APIKeyEnableDisableModal } from './APIKeyEnableDisableModal';
import { buildMenuAction } from './MenuActionItem';
import { redirectWithAlert } from '../../../../services/FlashService';
import { AlertType } from '../../../../components/Alert';

interface PropTypes {
  apiKey: APIKey;
}
const REDIRECT_URI = 'organization_settings/api_keys';

const RowActionsDropdown: React.FC<PropTypes> = ({ apiKey }) => {
  const [isEnableDisableModalOpen, setEnableDisableModalOpen] = useState(false);
  const [editAPIKey, setEditAPIKey] = useState(false);
  const isRevoked = apiKey.status === APIKeyStatus.Revoked;

  const handleEditKey = useCallback(() => setEditAPIKey(true), []);
  const handleRevokeAction = useCallback(
    () => setEnableDisableModalOpen(true),
    [],
  );

  const handleAPIKeyEnableDisableConfirm = useCallback(async () => {
    const newStatus = isRevoked ? APIKeyStatus.Active : APIKeyStatus.Revoked;
    const message = isRevoked
      ? 'The API Key was successfully re-enabled!'
      : 'The API Key was successfully revoked!';
    await APIKeyService.update(apiKey.id, apiKey.name, newStatus)
      .then(() => {
        redirectWithAlert(REDIRECT_URI, message);
      })
      .catch(() => {
        redirectWithAlert(
          REDIRECT_URI,
          `Failed to ${newStatus} API Key.`,
          AlertType.Danger,
        );
      });
  }, [apiKey.id, apiKey.name, isRevoked]);

  const handleUpdateAPIKey = useCallback(
    async (name: string) => {
      await APIKeyService.update(apiKey.id, name, apiKey.status)
        .then(() => {
          redirectWithAlert(REDIRECT_URI, 'API Key updated successfully.');
        })
        .catch(() => {
          redirectWithAlert(
            REDIRECT_URI,
            'Failed to update API Key.',
            AlertType.Danger,
          );
        })
        .finally(() => {
          setEditAPIKey(false);
        });
    },
    [apiKey.id, apiKey.status],
  );

  const menuActions = [
    buildMenuAction(handleEditKey, 'Edit Key Name', 'bi-pencil'),
    buildMenuAction(handleRevokeAction, 'Revoke', 'bi-x'),
  ];

  return (
    <>
      {!isRevoked ? (
        <Dropdown
          buttonIcon={{
            name: 'bi-three-dots',
            className: 'fs-4 text-info',
          }}
          menuPlacementClass='dropdown-menu-end'
          menuActions={menuActions}
        />
      ) : (
        <SpanTooltip
          text={
            <IconButton
              iconName='bi-arrow-repeat'
              color='borderless'
              size='sm'
              onClick={handleRevokeAction}
            />
          }
          tooltipText='Re-enable'
          trigger='hover'
        />
      )}

      {isEnableDisableModalOpen && (
        <APIKeyEnableDisableModal
          isOpen={isEnableDisableModalOpen}
          onConfirm={handleAPIKeyEnableDisableConfirm}
          setIsOpen={setEnableDisableModalOpen}
          apiKeyStatus={apiKey.status}
        />
      )}

      {editAPIKey && (
        <EditAPIKeyNameModal
          onSubmit={handleUpdateAPIKey}
          onClose={() => setEditAPIKey(false)}
          name={apiKey.name}
          token={apiKey.token}
        />
      )}
    </>
  );
};

export default memo(RowActionsDropdown);
